import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Mail, Scissors, Loader2, Copy, Star, BookMarked, Trash2, CornerDownLeft } from "lucide-react";

const LIMIT = 50;

export function extractSubjects(text) {
  return [...text.matchAll(/Oggetto(?:\s+(?:email|dell['’]email))?\s*\*{0,2}\s*[:\-–]\s*\*{0,2}\s*(.+)/gi)]
    .map((m) => m[1].split(/\s*\*?\(\d+\s*car|\s*\*{0,2}(?:Corpo|CTA)\*{0,2}\s*:/i)[0]
      .replace(/\*+/g, "").replace(/^["«“]|["»”]$/g, "").trim())
    .filter(Boolean);
}

const tone = (n) => (n <= 40 ? "#22c55e" : n <= LIMIT ? "#f59e0b" : "#f43f5e");
const len = (s) => [...s].length;

export function useSubjectFavorites() {
  const [favs, setFavs] = useState([]);
  const load = useCallback(() => api.get("/subject-favorites").then((r) => setFavs(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const find = (s) => favs.find((f) => f.subject.trim() === s.trim());
  const toggle = async (subject, sector) => {
    const ex = find(subject);
    if (ex) {
      await api.delete(`/subject-favorites/${ex.id}`);
      toast.success("Rimosso dalla libreria");
    } else {
      await api.post("/subject-favorites", { subject: subject.trim(), sector });
      toast.success("Salvato nella libreria oggetti");
    }
    load();
  };
  const remove = async (id) => { await api.delete(`/subject-favorites/${id}`); load(); };
  return { favs, find, toggle, remove };
}

export function EmailSubjectChecker({ subjects, onChange, fav, sector, active, setActive }) {
  if (!subjects.length) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 mb-3 space-y-2.5" data-testid="email-subject-checker">
      <div className="flex items-center gap-2 text-xs text-slate-300 font-semibold">
        <Mail className="h-3.5 w-3.5" /> Controllo oggetto email · consigliato max {LIMIT} caratteri
      </div>
      {subjects.map((s, i) => (
        <SubjectRow key={i} index={i} value={s} onChange={(v) => onChange(i, v)} active={active === i} onFocus={() => setActive(i)}
          starred={!!fav.find(s)} onStar={() => fav.toggle(s, sector)} />
      ))}
    </div>
  );
}

function SubjectRow({ index, value, onChange, starred, onStar, active, onFocus }) {
  const [busy, setBusy] = useState(false);
  const n = len(value);
  const c = tone(n);
  const shorten = async () => {
    setBusy(true);
    try {
      const r = await api.post("/ai/shorten-subject", { subject: value, max_len: LIMIT });
      onChange(r.data.subject);
      toast.success(`Oggetto accorciato a ${r.data.length} caratteri`);
    } catch { toast.error("Impossibile accorciare l'oggetto"); }
    finally { setBusy(false); }
  };
  const ib = "h-8 w-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 transition-colors shrink-0";
  return (
    <div data-testid={`email-subject-row-${index}`}>
      <div className="flex items-center gap-2">
        <input value={value} onChange={(e) => onChange(e.target.value)} onFocus={onFocus} data-testid={`email-subject-input-${index}`}
          className="flex-1 min-w-0 bg-black/30 border rounded-lg px-3 py-2 text-sm focus:outline-none transition-colors"
          style={{ borderColor: `${c}80`, boxShadow: active ? `0 0 0 1px ${c}55` : "none" }} />
        <span className="text-xs font-mono font-semibold w-12 text-right" style={{ color: c }} data-testid={`email-subject-count-${index}`}>
          {n}/{LIMIT}
        </span>
        <button onClick={shorten} disabled={busy} data-testid={`email-subject-shorten-${index}`}
          className="flex items-center gap-1 text-xs px-2.5 py-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50 transition-colors whitespace-nowrap">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Scissors className="h-3.5 w-3.5" />} Accorcia con AI
        </button>
        <button onClick={onStar} title={starred ? "Rimuovi dai preferiti" : "Salva nei preferiti"} data-testid={`email-subject-star-${index}`} className={ib}>
          <Star className="h-3.5 w-3.5" style={starred ? { color: "#facc15", fill: "#facc15" } : undefined} />
        </button>
        <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Oggetto copiato"); }} data-testid={`email-subject-copy-${index}`} className={ib}>
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="h-1 mt-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full transition-[width,background-color] duration-300" style={{ width: `${Math.min(100, (n / LIMIT) * 100)}%`, background: c }} />
      </div>
    </div>
  );
}

export function SubjectLibrary({ fav, canInsert, onInsert, kind }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] mb-3" data-testid={`subject-library-${kind}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`subject-library-toggle-${kind}`}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-xs font-semibold text-slate-300 hover:text-white transition-colors">
        <BookMarked className="h-3.5 w-3.5 text-yellow-400" /> Libreria oggetti email
        <span className="ml-auto text-slate-500" data-testid={`subject-library-count-${kind}`}>{fav.favs.length} salvati · {open ? "chiudi" : "apri"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1.5 max-h-56 overflow-y-auto">
          {fav.favs.length === 0 && <p className="text-xs text-slate-500">Nessun oggetto salvato. Usa la stella accanto a un oggetto generato.</p>}
          {fav.favs.map((f) => (
            <div key={f.id} className="flex items-center gap-2 text-sm bg-black/20 rounded-lg px-2.5 py-1.5" data-testid={`subject-fav-${f.id}`}>
              <Star className="h-3 w-3 shrink-0" style={{ color: "#facc15", fill: "#facc15" }} />
              <span className="flex-1 min-w-0 truncate">{f.subject}</span>
              <span className="text-[10px] font-mono" style={{ color: tone(len(f.subject)) }}>{len(f.subject)}</span>
              {canInsert && (
                <button onClick={() => onInsert(f.subject)} data-testid={`subject-fav-insert-${f.id}`} title="Inserisci nell'email generata"
                  className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-white/5 hover:bg-white/10"><CornerDownLeft className="h-3 w-3" /> Inserisci</button>
              )}
              <button onClick={() => { navigator.clipboard.writeText(f.subject); toast.success("Oggetto copiato"); }} data-testid={`subject-fav-copy-${f.id}`}
                className="p-1 rounded-md hover:bg-white/10"><Copy className="h-3 w-3" /></button>
              <button onClick={() => fav.remove(f.id)} data-testid={`subject-fav-delete-${f.id}`} className="p-1 rounded-md hover:bg-rose-500/20 text-rose-300"><Trash2 className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
